import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

export const Clear2PrimeNGTheme = definePreset(Aura, {
  primitive: {
    cmg: {
      primaryLight: '#32a4ac',
      primary: '#138890',
      primaryDark: '#047880',
      secondaryLight: '#86ba41',
      secondaryDark: '#639a33',
      aiPrimary: '#047880',
      aiSecondary: '#85b036',
    },
    grays: {
      30: '#FCFCFC',
      40: '#F7F7F7',
      50: '#F4F4F5',
      75: '#EDEDED',
      100: '#EEEEEE',
      200: '#E4E4E4',
      300: '#CED4DB',
      400: '#A9A8A9',
      500: '#707070',
      510: '#717171',
      600: '#636363',
      700: '#495057',
      800: '#4D4D4D',
      900: '#3B3B3B',
      950: '#202020',
    },
  },
  components: {
    button: {
      root: {
        borderRadius: '2rem',
        iconOnlyWidth: '1.8rem',
        paddingX: '1.75rem',
        paddingY: '0.6rem',
        sm: {
          paddingX: '0.7175rem',
          paddingY: '0.246rem',
        },
        lg: {
          fontSize: '1.25rem',
          paddingX: '1.89rem',
          paddingY: '0.648rem',
        },
        label: { fontWeight: '700' },
      },
      colorScheme: {
        light: {
          root: {
            contrast: {
              background: 'transparent',
              color: '{cmg.primary}',
              borderColor: 'transparent',
              hoverBackground: 'rgba(50, 164, 172, 0.1)',
              hoverColor: '{cmg.primary}',
              hoverBorderColor: 'transparent',
              focusRing: {
                color: '{cmg.primary}',
              },
            },
            danger: {
              background: 'rgb(255, 224, 223)',
              color: 'rgb(235, 16, 0)',
              borderColor: 'rgb(255, 224, 223)',
              hoverBackground: 'rgb(255, 224, 223)',
              hoverColor: 'rgb(235, 16, 0)',
              hoverBorderColor: 'rgb(255, 224, 223)',
              focusRing: {
                color: 'rgb(255, 134, 127)',
              },
            },
            secondary: {
              background: '#32a4ac1a',
              color: '#047880',
              borderColor: 'rgba(50, 164, 172, .1)',
              hoverBackground: '#32a4ac1a',
              hoverColor: '#047880',
              hoverBorderColor: 'rgba(50, 164, 172, .1)',
              activeBackground: '#32a4ac55',
              activeBorderColor: 'rgba(50, 164, 172, .3)',
              activeColor: '#047880',
              focusRing: {
                color: '{cmg.primary}',
              },
            },
          },
        },
      },
    },
    card: {
      root: {
        borderRadius: '{borderRadius.md}',
        shadow: 'none',
      },
    },
    datatable: {
      colorScheme: {
        light: {
          root: {
            borderColor: '#cfcfcf',
          },
          header: {
            background: '{grays.75}',
            borderWidth: '1px 1px 0',
            color: '{grays.950}',
          },
          headerCell: {
            background: '{grays.40}',
          },
          row: {
            hoverBackground: '#f8f9fa',
            stripedBackground: '#fcfcfc',
          },
          footerCell: {
            background: '{grays.40}',
          },
        },
      },
    },
    datepicker: {
      dropdown: {
        width: '2.74rem',
      },
    },
    message: {
      root: {
        borderWidth: '0 0 0 6px',
      },
      content: {
        padding: '1.3rem 1.8rem',
      },
      icon: {
        size: '1.5rem',
      },
      colorScheme: {
        light: {
          info: {
            background: '#edf6fa',
            color: '#387780',
          },
          warn: {
            background: '#fefaed',
            color: '#cfa244',
          },
        },
      },
    },
    radiobutton: {
      root: {
        width: '18px',
        height: '18px',
      },
      icon: {
        size: '10px',
      },
    },
    toast: {
      root: {
        borderWidth: '0 0 0 6px',
      },
      content: {
        padding: '1rem',
      },
      summary: {
        fontWeight: '700',
      },
      colorScheme: {
        light: {
          info: {
            background: '#edf6fa',
            borderColor: '#387780',
            color: '#387780',
            detailColor: '#387780',
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
          },
          success: {
            background: '#e4f8f0',
            borderColor: '#1ea97c',
            color: '#1ea97c',
            detailColor: '#1ea97c',
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
          },
          warn: {
            background: '#fefaed',
            borderColor: '#cfa244',
            color: '#cfa244',
            detailColor: '#cfa244',
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
          },
          error: {
            background: '#ffe7e6',
            borderColor: '#ff5757',
            color: '#ff5757',
            detailColor: '#ff5757',
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
  },
  semantic: {
    colorScheme: {
      light: {
        formField: {
          hoverBorderColor: '{primary.color}',
          borderColor: '{grays.300}',
          disabledBackground: '{grays.100}',
          shadow: 'none',
        },
        mask: { background: 'rgba(0,0,0,0.1)' },
      },
    },
    disabledOpacity: '1',
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: 'none',
    },
    formField: {
      borderRadius: '3px',
      paddingX: '0.75rem',
      paddingY: '0.35rem',
      focusRing: {
        width: '2px',
        style: 'solid',
        color: '#13889040',
        offset: '0',
        shadow: 'none',
      },
    },
    navigation: {
      item: {
        padding: '0.95rem 1.25rem',
      },
    },
    primary: {
      50: '#eff6ff',
      100: '#d1e4e5',
      200: '#9ed2d6',
      300: '#60c5cd',
      400: '#2db4be',
      500: '{cmg.primary}',
      600: '#19666b',
      700: '#174649',
      800: '#142d2f',
      900: '#0b1314',
      950: '#070909',
    },
  },
});
